import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import Pagination from '../../components/Pagination';
import ConfirmDialog from '../../components/ConfirmDialog';
import AddressQrCode from './AddressQrCode';
import WalletAddressEditModal from './WalletAddressEditModal';
import {
  ADDRESS_TYPE_FILTER_OPTIONS,
  STATUS_FILTER_OPTIONS,
  buildCoinPayTypePageParams,
  buildWalletAddressPageParams,
  formatAddressType,
  formatStatusLabel,
  normalizeCoinPayTypes,
} from './walletAddressConstants';

const EMPTY_FILTERS = {
  status: '',
  addressType: '',
  cryptoCurrency: '',
  address: '',
};

const TABLE_COLUMNS = [
  '地址類型',
  '數字貨幣',
  '地址',
  '地址二維碼',
  '狀態',
  '順序',
  '操作',
];

export default function WalletAddressManagePage() {
  const [coinPayTypes, setCoinPayTypes] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  async function loadCoinPayTypes() {
    try {
      const data = await adminApi.coinPayTypePage(buildCoinPayTypePageParams());
      setCoinPayTypes(normalizeCoinPayTypes(data?.records));
    } catch (err) {
      setError(err.message || '載入幣種失敗');
      setCoinPayTypes([]);
    }
  }

  async function loadRecords(pageNo = 1, nextFilters = filters) {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.walletAddressPage(buildWalletAddressPageParams(pageNo, nextFilters));
      setRecords(data?.records || []);
      setPage(data?.current || pageNo);
      setTotalPages(data?.pages || 1);
      setTotal(data?.total ?? 0);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadCoinPayTypes();
    loadRecords(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handleSearch(event) {
    event.preventDefault();
    setMessage('');
    loadRecords(1, filters);
  }

  function handleReset() {
    const reset = { ...EMPTY_FILTERS };
    setFilters(reset);
    setMessage('');
    loadRecords(1, reset);
  }

  function openCreate() {
    setEditRow(null);
    setEditOpen(true);
    setMessage('');
  }

  function openEdit(row) {
    setEditRow(row);
    setEditOpen(true);
    setMessage('');
  }

  async function handleSave(payload) {
    setSubmitting(true);
    setError('');
    try {
      if (payload.id) {
        await adminApi.updateWalletAddress(payload);
        setMessage('已更新');
      } else {
        await adminApi.createWalletAddress(payload);
        setMessage('已新增');
      }
      setEditOpen(false);
      setEditRow(null);
      await loadRecords(page, filters);
    } catch (err) {
      setError(err.message || '保存失敗');
      throw err;
    } finally {
      setSubmitting(false);
    }
  }

  async function handleToggleStatus(row) {
    if (!row?.id) {
      return;
    }
    const nextStatus = row.status === 1 ? 0 : 1;
    setTogglingId(row.id);
    setError('');
    try {
      await adminApi.updateWalletAddress({ id: row.id, status: nextStatus });
      setRecords((prev) =>
        prev.map((item) => (item.id === row.id ? { ...item, status: nextStatus } : item))
      );
      setMessage(nextStatus === 1 ? '已啟用' : '已禁用');
    } catch (err) {
      setError(err.message || '更新狀態失敗');
    } finally {
      setTogglingId(null);
    }
  }

  async function handleConfirmDelete() {
    if (!deleteTarget?.id) {
      return;
    }
    setDeleting(true);
    setError('');
    try {
      await adminApi.removeWalletAddresses([deleteTarget.id]);
      setDeleteTarget(null);
      setMessage('已刪除');
      await loadRecords(page, filters);
    } catch (err) {
      setError(err.message || '刪除失敗');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="page-card">
      <div className="section-header">
        <h2>充提：在線地址</h2>
      </div>

      <form className="filter-bar filter-bar-wrap" onSubmit={handleSearch}>
        <div className="form-field">
          <label htmlFor="waStatus">錢包狀態</label>
          <select
            id="waStatus"
            value={filters.status}
            onChange={(e) => updateFilter('status', e.target.value)}
          >
            {STATUS_FILTER_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="waAddressType">地址類型</label>
          <select
            id="waAddressType"
            value={filters.addressType}
            onChange={(e) => updateFilter('addressType', e.target.value)}
          >
            {ADDRESS_TYPE_FILTER_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="waCryptoCurrency">數字貨幣</label>
          <select
            id="waCryptoCurrency"
            value={filters.cryptoCurrency}
            onChange={(e) => updateFilter('cryptoCurrency', e.target.value)}
          >
            <option value="">全部</option>
            {coinPayTypes.map((coin) => (
              <option key={coin.code} value={coin.code}>
                {coin.code}
              </option>
            ))}
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="waAddress">錢包地址</label>
          <input
            id="waAddress"
            type="text"
            value={filters.address}
            onChange={(e) => updateFilter('address', e.target.value)}
            placeholder="請輸入..."
          />
        </div>
        <div className="filter-actions">
          <button type="submit" className="btn btn-primary">
            搜索
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleReset}>
            重置
          </button>
          <button type="button" className="btn btn-primary" onClick={openCreate}>
            新增
          </button>
        </div>
      </form>

      {loading && <p>載入中…</p>}
      {error && <div className="alert alert-error">{error}</div>}
      {message && <div className="alert alert-success">{message}</div>}

      {!loading && <p className="page-desc">共 {total} 筆</p>}

      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              {TABLE_COLUMNS.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!loading && records.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="empty-cell">
                  尚無紀錄
                </td>
              </tr>
            ) : (
              records.map((row) => (
                <tr key={row.id}>
                  <td>{formatAddressType(row.addressType)}</td>
                  <td className="mono-cell">{row.cryptoCurrency || '—'}</td>
                  <td className="mono-cell wallet-address-cell">{row.address}</td>
                  <td className="wallet-address-qr-cell">
                    <AddressQrCode value={row.address} size={72} />
                  </td>
                  <td>
                    <span
                      className={`status-badge ${
                        row.status === 1 ? 'status-badge--enabled' : 'status-badge--disabled'
                      }`}
                    >
                      {formatStatusLabel(row.status)}
                    </span>
                  </td>
                  <td>{row.sort ?? 0}</td>
                  <td>
                    <div className="table-actions">
                      <button
                        type="button"
                        className="btn btn-success btn-sm"
                        onClick={() => openEdit(row)}
                      >
                        修改
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger-outline btn-sm"
                        onClick={() => setDeleteTarget(row)}
                      >
                        刪除
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm ${row.status === 1 ? 'btn-danger-outline' : 'btn-success'}`}
                        disabled={togglingId === row.id}
                        onClick={() => handleToggleStatus(row)}
                      >
                        {row.status === 1 ? '禁用' : '啟用'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={(p) => loadRecords(p, filters)} />

      <WalletAddressEditModal
        open={editOpen}
        row={editRow}
        coinPayTypes={coinPayTypes}
        submitting={submitting}
        onClose={() => {
          if (!submitting) {
            setEditOpen(false);
            setEditRow(null);
          }
        }}
        onSubmit={handleSave}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="刪除在線地址"
        message={
          deleteTarget
            ? `確定刪除地址「${deleteTarget.address}」？`
            : ''
        }
        confirmLabel={deleting ? '刪除中…' : '確認刪除'}
        confirming={deleting}
        onClose={() => !deleting && setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}
